import { Search, Xmark } from 'iconoir-react';
import { ChangeEvent, FC, KeyboardEvent } from 'react';

import { EVENT_KEY } from '@savgroup-front-common/constants/shared';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@savgroup-front-common/primitive/ui/input-group';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';

interface ClearableSearchInputGroupProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clearAriaLabel: string;
  isLoading?: boolean;
  autoFocus?: boolean;
  className?: string;
  inputDataTestId?: string;
  clearDataTestId?: string;
  onFocus?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export const ClearableSearchInputGroup: FC<ClearableSearchInputGroupProps> = ({
  value,
  onChange,
  placeholder,
  clearAriaLabel,
  isLoading = false,
  autoFocus,
  className,
  inputDataTestId,
  clearDataTestId,
  onFocus,
  onKeyDown,
}) => {
  const hasQuery = value.trim().length > 0;

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === EVENT_KEY.ENTER) {
      event.currentTarget.blur();
    }

    onKeyDown?.(event);
  }

  return (
    <InputGroup className={cn('bg-surface-base-default', className)}>
      <InputGroupAddon className="stext-muted-default">
        {isLoading ? <Spinner /> : <Search />}
      </InputGroupAddon>
      <InputGroupInput
        data-testid={inputDataTestId}
        placeholder={placeholder}
        className="stext-base-default placeholder:stext-muted-default"
        value={value}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
      />
      {hasQuery && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            aria-label={clearAriaLabel}
            data-testid={clearDataTestId}
            onClick={() => onChange('')}
          >
            <Xmark />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
};
